import { emptySplitApi as api } from "./emptyApi"

/**
 * Meta Advanced (APP-548): the current company's Meta system-user credential,
 * one per company. Hand-written (not codegen). The token goes up once,
 * write-only, on a set; no response carries it.
 */

/** Why Meta reads and edits skip the system user now (APP-566 T3). */
export interface MetaBrandCredentialSkip {
  reason:
    | "not_set"
    | "paused"
    | "invalid"
    | "pipeline_off"
    | "no_ad_account"
    | "scope_missing"
  /** User-facing, API-owned: the page shows it verbatim. */
  detail: string
}

/** `GET` / `PUT /meta-brand-credential`. Status only, never the token. */
export interface MetaBrandCredentialStatus {
  connected: boolean
  /** The system user's name. Super admins only: the API strips it for everyone else. */
  systemUserName?: string | null
  /** Recommended permissions the token lacks; the matching features stay off. */
  missingScopes: string[]
  /** When the credential was set or last replaced. */
  setAt?: string | null
  /** When Meta last confirmed the token. */
  verifiedAt?: string | null
  /** The last recorded failure on this token (a fixed text). */
  lastError?: string | null
  /** Whether the company's Meta edits run on this credential (the per-company switch). */
  editsEnabled: boolean
  /** When a super admin turned the switch on. */
  editsEnabledAt?: string | null
  // APP-566 T3, optional: a pre-T3 API omits them and reads as tier order off.
  /** When a super admin paused the system user (APP-566 T3). */
  pausedAt?: string | null
  /** When Meta refused the stored token; skipped until replaced. */
  invalidAt?: string | null
  /** metaCredentials.tierOrder: Pause replaces the edit switch. */
  tierOrder?: boolean
  /** While tierOrder is on: why reads and edits skip the system user now. */
  systemUserSkip?: MetaBrandCredentialSkip | null
  /** APP-566 T5: true only when, without the system user, reads or edits would run on the company's account user. */
  accountUserNext?: boolean
}

/**
 * `PUT /meta-brand-credential` (super admin). `companyId` is the company the
 * page shows: the API refuses (409) a write for any company but the session's,
 * which another tab may have switched.
 */
export interface SetMetaBrandCredentialRequest {
  companyId: string
  systemUserAccessToken: string
}

/** `DELETE /meta-brand-credential?companyId=` (super admin), as for a set. */
export interface RemoveMetaBrandCredentialRequest {
  companyId: string
}

export interface RemoveMetaBrandCredentialResponse {
  removed: boolean
}

/** `PUT /meta-brand-credential/edits` (super admin), as for a set. */
export interface SetMetaBrandCredentialEditsRequest {
  companyId: string
  enabled: boolean
}

/** `PATCH /meta-brand-credential/pause` (super admin), as for a set. */
export interface SetMetaBrandCredentialPausedRequest {
  companyId: string
  paused: boolean
}

export const metaBrandCredentialApi = api.injectEndpoints({
  endpoints: build => ({
    /** Any company user. */
    getMetaBrandCredential: build.query<MetaBrandCredentialStatus, void>({
      query: () => "/meta-brand-credential",
      providesTags: ["MetaBrandCredential"],
    }),

    /**
     * Super admin only, throttled 5/min. The API verifies the token with Meta
     * before it stores it; a 400 carries a user-facing `message`.
     */
    setMetaBrandCredential: build.mutation<
      MetaBrandCredentialStatus,
      SetMetaBrandCredentialRequest
    >({
      query: body => ({
        url: "/meta-brand-credential",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["MetaBrandCredential"],
    }),

    /** Super admin only, throttled 5/min. */
    removeMetaBrandCredential: build.mutation<
      RemoveMetaBrandCredentialResponse,
      RemoveMetaBrandCredentialRequest
    >({
      query: ({ companyId }) => ({
        url: "/meta-brand-credential",
        method: "DELETE",
        params: { companyId },
      }),
      invalidatesTags: ["MetaBrandCredential"],
    }),

    /**
     * Super admin only, throttled 5/min. Moves the company's Meta edits to
     * the credential, or back to the login; a 400 carries a user-facing `message`.
     */
    setMetaBrandCredentialEdits: build.mutation<
      MetaBrandCredentialStatus,
      SetMetaBrandCredentialEditsRequest
    >({
      query: body => ({
        url: "/meta-brand-credential/edits",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["MetaBrandCredential"],
    }),

    /** Super admin only, throttled 5/min; a 400 carries a user-facing `message`. */
    setMetaBrandCredentialPaused: build.mutation<
      MetaBrandCredentialStatus,
      SetMetaBrandCredentialPausedRequest
    >({
      query: body => ({
        url: "/meta-brand-credential/pause",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["MetaBrandCredential"],
    }),
  }),
})

export const {
  useGetMetaBrandCredentialQuery,
  useSetMetaBrandCredentialMutation,
  useRemoveMetaBrandCredentialMutation,
  useSetMetaBrandCredentialEditsMutation,
  useSetMetaBrandCredentialPausedMutation,
} = metaBrandCredentialApi
